import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import { authActions } from "store/auth-slice";
import { useAlert } from "components/UI_components/Alert";
import { BASE_URL } from "../../utils/constant";

// Assets
import base_background from "../../assets/Base (1).png";
import arrow from "../../assets/Vector (1).png";
import main_logo from "../../assets/SM_Full_White-100 1 (1).png";
import arrow_forward from "../../assets/arrow_forward.png";
import character4 from "../../assets/roles/member.svg";
import character2 from "../../assets/roles/character-1.svg";
import character3 from "../../assets/roles/character-2.svg";
import character5 from "../../assets/roles/Character (1).svg";
import character6 from "../../assets/roles/character-1 (1).svg";
import memberIcon from "../../assets/roles/member.svg";
import ownerIcon from "../../assets/roles/owner.svg";
// Step 1: Owner/Individual Selection
function UserTypeStep({ onNext }) {
  const dispatch = useDispatch();
  const [userType, setUserType] = useState("");
  const [selectedProfession, setSelectedProfession] = useState(-1);
  const [showProfessionStep, setShowProfessionStep] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const professions = [
    { profession: "Contractor", source: character2 },
    { profession: "Supervisor", source: character6 },
    { profession: "Manager", source: character5 },
    { profession: "Architect", source: character3 },
    { profession: "Engineer", source: character3 },
    { profession: "Vendor", source: character4 },
    { profession: "Employee", source: character4 },
    { profession: "Others", source: character4 },
    { profession: "Custom", source: character6 },
  ];

  const validationSchema = yup.object().shape({
    userType: yup.string().required("Please select an option"),
    profession: yup.string().when("userType", (userType, schema) => {
      if (userType === "individual") {
        return schema.required("Profession is Required");
      }
      return schema;
    }),
    customProfession: yup
      .string()
      .when(["userType", "profession"], (values, schema) => {
        const [userTypeVal, professionVal] = values;
        if (userTypeVal === "individual" && professionVal === "Custom") {
          return schema.required("Custom profession is required");
        }
        return schema;
      }),
  });

  const formik = useFormik({
    initialValues: { userType: "", profession: "", customProfession: "" },
    validationSchema,
    onSubmit: async (values) => {
      if (isSubmitting) return;
      setIsSubmitting(true);

      try {
        let finalProfession;

        if (values.userType === "owner") {
          finalProfession = "owner";
        } else {
          finalProfession =
            values.profession === "Custom"
              ? values.customProfession
              : values.profession;
        }

        dispatch(authActions.login({ profession: finalProfession }));
        onNext();
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const handleUserTypeSelect = (type) => {
    if (isSubmitting) return;

    setUserType(type);
    formik.setFieldValue("userType", type);

    if (type === "individual") {
      setShowProfessionStep(true);
      // Clear profession fields when switching to individual
      formik.setFieldValue("profession", "");
      formik.setFieldValue("customProfession", "");
      setSelectedProfession(-1);
    } else {
      setShowProfessionStep(false);
    }
  };

  const handleNext = () => {
    if (isSubmitting) return;

    if (userType === "owner") {
      // Submit directly for owner
      formik.handleSubmit();
    } else if (userType === "individual" && showProfessionStep) {
      // Submit from profession selection
      formik.handleSubmit();
    } else if (userType === "individual" && !showProfessionStep) {
      setShowProfessionStep(true);
    }
  };

  const handleBack = () => {
    if (isSubmitting) return;

    if (showProfessionStep) {
      setShowProfessionStep(false);
    }
  };

  const canProceed = () => {
    if (!showProfessionStep) {
      return userType !== "";
    } else {
      return (
        formik.values.profession !== "" &&
        (formik.values.profession !== "Custom" ||
          formik.values.customProfession !== "")
      );
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleNext();
      }}
      className="flex flex-col align-middle justify-between px-10 pb-5"
      style={{ width: "80%" }}
    >
      {/* Progress - Update to show 2 steps for individual users */}
      <div className="flex items-center justify-center mb-6">
        <div className="flex items-center">
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold text-white"
            style={{ backgroundColor: "#3B0069" }}
          >
            1
          </div>
          {userType === "individual" && (
            <>
              <div
                className={`w-12 h-1 ${
                  showProfessionStep ? "bg-purple-800" : "bg-gray-300"
                }`}
                style={{
                  backgroundColor: showProfessionStep ? "#3B0069" : "#D1D5DB",
                }}
              ></div>
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                  showProfessionStep
                    ? "text-white"
                    : "bg-gray-300 text-gray-600"
                }`}
                style={{
                  backgroundColor: showProfessionStep ? "#3B0069" : "#D1D5DB",
                }}
              >
                1b
              </div>
              <div className="w-12 h-1 bg-gray-300"></div>
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold bg-gray-300 text-gray-600">
                2
              </div>
            </>
          )}
          {userType === "owner" && (
            <>
              <div className="w-12 h-1 bg-gray-300"></div>
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold bg-gray-300 text-gray-600">
                2
              </div>
            </>
          )}
        </div>
      </div>

      <span
        className="py-5 font-extrabold text-2xl"
        style={{ color: "#3B0069", fontSize: 32 }}
      >
        Create an Account
      </span>
      <span
        style={{ fontSize: 16, fontWeight: 200, opacity: 0.8, marginTop: -12 }}
      >
        Already have an account?{" "}
        <Link
          to="/login"
          style={{ color: "#3B0069", textDecoration: "underline" }}
        >
          Sign in
        </Link>
      </span>

      {!showProfessionStep && (
        <>
          <span
            className="py-2"
            style={{ fontSize: 24, fontWeight: 700, marginTop: 20 }}
          >
            I want to onboard as:
          </span>
          <div className="flex flex-row gap-3 mt-4">
            {[
              {
                key: "owner",
                label: "Business Owner",
                desc: "Create and manage your company, projects, teams and vendors.",
                img: ownerIcon,
              },
              {
                key: "individual",
                label: "Team Member",
                desc: "Join your organization and collaborate on assigned projects.",
                img: memberIcon,
              },
            ].map((opt) => (
              <div
                key={opt.key}
                onClick={() => handleUserTypeSelect(opt.key)}
                className={`flex flex-col w-[28rem] items-center ${
                  isSubmitting
                    ? "cursor-not-allowed opacity-50"
                    : "cursor-pointer"
                }`}
                style={{
                  padding: 20,
                  borderRadius: 12,
                  border:
                    userType === opt.key
                      ? "2px solid #3B0069"
                      : "2px solid #E5E7EB",
                  backgroundColor:
                    userType === opt.key ? "#3B00691A" : "#F9FAFB",
                  minWidth: 140,
                  transition: "all 0.3s ease",
                }}
              >
                <img
                  src={opt.img}
                  alt={opt.label}
                  className="w-16 h-16 mb-2"
                  style={{
                    background: userType === opt.key ? "#3B006999" : "#6F6F6F",
                    borderRadius: 11,
                  }}
                />
                <span
                  style={{
                    fontWeight: 600,
                    color: userType === opt.key ? "#3B0069" : "#374151",
                    fontSize: 16,
                  }}
                >
                  {opt.label}
                </span>
                <span style={{ fontSize: 12, color: "#6B7280", marginTop: 4 }}>
                  {opt.desc}
                </span>
              </div>
            ))}
          </div>
          {formik.touched.userType && formik.errors.userType && (
            <span className="text-red-500 text-sm mt-2">
              {formik.errors.userType}
            </span>
          )}
        </>
      )}

      {showProfessionStep && userType === "individual" && (
        <>
          <span
            className="py-2"
            style={{ fontSize: 24, fontWeight: 700, marginTop: 20 }}
          >
            Select Your Profession
          </span>

          <div
            className="grid grid-cols-3 gap-4"
            style={{ width: "100%", marginTop: "20px" }}
          >
            {professions.map((profession, index) => (
              <div
                className={`flex flex-col items-center p-4 rounded-lg transition-all duration-300 hover:shadow-md ${
                  isSubmitting
                    ? "cursor-not-allowed opacity-50"
                    : "cursor-pointer"
                }`}
                key={index}
                onClick={() => {
                  if (isSubmitting) return;

                  formik.setFieldValue("profession", profession.profession);
                  setSelectedProfession(index);
                  if (profession.profession !== "Custom") {
                    formik.setFieldValue("customProfession", "");
                  }
                }}
                style={{
                  border:
                    selectedProfession === index
                      ? "2px solid #3B0069"
                      : "2px solid #E5E7EB",
                  backgroundColor:
                    selectedProfession === index ? "#3B00691A" : "#F9FAFB",
                }}
              >
                <img
                  src={profession.source}
                  alt={profession.profession}
                  className="w-12 h-12 mb-2"
                  style={{
                    background:
                      selectedProfession === index ? "#3B006999" : "#6F6F6F",
                    borderRadius: "8px",
                  }}
                />
                <span
                  className="text-center text-sm font-medium"
                  style={{
                    color: selectedProfession === index ? "#3B0069" : "#374151",
                  }}
                >
                  {profession.profession}
                </span>
              </div>
            ))}
          </div>

          {formik.values.profession === "Custom" && (
            <div className="mt-6">
              <label
                htmlFor="customProfession"
                style={{
                  fontSize: "16px",
                  color: "#374151",
                  fontWeight: "500",
                  display: "block",
                  marginBottom: "8px",
                }}
              >
                Enter Your Profession
              </label>
              <input
                id="customProfession"
                type="text"
                placeholder="e.g. Site Engineer, Project Coordinator..."
                value={formik.values.customProfession}
                onChange={(e) =>
                  formik.setFieldValue("customProfession", e.target.value)
                }
                onBlur={formik.handleBlur}
                name="customProfession"
                className="bg-gray-200 rounded p-2 px-8 w-full"
                disabled={isSubmitting}
              />
              {formik.touched.customProfession &&
                formik.errors.customProfession && (
                  <span className="text-red-500 text-xs mt-1 block">
                    {formik.errors.customProfession}
                  </span>
                )}
            </div>
          )}

          {formik.touched.profession && formik.errors.profession && (
            <span className="text-red-500 text-sm mt-2">
              {formik.errors.profession}
            </span>
          )}
        </>
      )}

      {/* Navigation */}
      <div className="flex gap-4 w-full mt-10" style={{ width: "80%" }}>
        {showProfessionStep && userType === "individual" && (
          <button
            type="button"
            onClick={handleBack}
            disabled={isSubmitting}
            className={`py-3 px-8 rounded-lg flex-1 border-2 text-lg transition-colors ${
              isSubmitting
                ? "border-gray-400 text-gray-400 cursor-not-allowed"
                : "hover:bg-purple-50"
            }`}
            style={{
              borderColor: isSubmitting ? "#9CA3AF" : "#3B0069",
              color: isSubmitting ? "#9CA3AF" : "#3B0069",
            }}
          >
            Back
          </button>
        )}

        <button
          type="submit"
          disabled={!canProceed() || isSubmitting}
          className={`py-3 px-8 rounded-lg flex-1 flex items-center justify-center gap-2 transition-colors ${
            canProceed() && !isSubmitting
              ? "hover:opacity-90"
              : "cursor-not-allowed"
          }`}
          style={{
            backgroundColor:
              canProceed() && !isSubmitting ? "#3B0069" : "#9CA3AF",
          }}
        >
          <span className="text-white text-lg">
            {isSubmitting
              ? "Processing..."
              : userType === "owner" ||
                  (userType === "individual" && showProfessionStep)
                ? "Next"
                : "Next"}
          </span>
          {!isSubmitting && (
            <img src={arrow_forward} alt="" className="w-6 h-6 rounded-full" />
          )}
        </button>
      </div>
    </form>
  );
}

// Step 2: User Details (first name, last name, phone)
function DetailsStep({ onBack }) {
  const { addAlert } = useAlert();
  const userDetails = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function addUserDetails(first, last, phone) {
    if (isSubmitting) return;
    setIsSubmitting(true);

    const token = localStorage.getItem("access");
    const headers = new Headers({
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    });
    const body = JSON.stringify({
      first_name: first,
      last_name: last,
      contact_number: phone,
      profession: userDetails.profession,
      language: "English",
    });
    try {
      const res = await fetch(`${BASE_URL}/auth/create-userdetails/`, {
        method: "POST",
        headers,
        body,
      });
      const result = await res.json();
      if (result.status) {
        navigate("/welcome");
      } else {
        throw new Error(result.message);
      }
    } catch (err) {
      addAlert(err.message, "error");
    } finally {
      setIsSubmitting(false);
    }
  }

  const validationSchema = yup.object().shape({
    first_name: yup.string().required("First Name is Required"),
    last_name: yup.string().required("Last Name is Required"),
    phone: yup
      .string()
      .matches(/^[0-9]{10}$/, "Must be exactly 10 digits")
      .required("Phone Number is Required"),
  });

  const formik = useFormik({
    initialValues: { first_name: "", last_name: "", phone: "" },
    validationSchema,
    onSubmit: (vals) =>
      addUserDetails(vals.first_name, vals.last_name, vals.phone),
  });

  return (
    <form
      onSubmit={formik.handleSubmit}
      className="flex flex-col px-10 pb-5"
      style={{ width: "80%", maxHeight: "85vh", overflow: "visible" }}
    >
      {/* Progress */}
      <div className="flex items-center justify-center mb-4">
        <div className="flex items-center">
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold text-white"
            style={{ backgroundColor: "#3B0069" }}
          >
            1
          </div>
          <div
            className="w-12 h-1"
            style={{ backgroundColor: "#3B0069" }}
          ></div>
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold text-white"
            style={{ backgroundColor: "#3B0069" }}
          >
            2
          </div>
        </div>
      </div>

      <span
        className="py-3 font-extrabold text-2xl"
        style={{ color: "#3B0069", fontSize: 28 }}
      >
        Create an Account
      </span>
      <span
        style={{ fontSize: 16, fontWeight: 200, opacity: 0.8, marginTop: -8 }}
      >
        Already have an account?{" "}
        <Link
          to="/login"
          style={{ color: "#3B0069", textDecoration: "underline" }}
        >
          Sign in
        </Link>
      </span>
      <span
        className="py-2"
        style={{ fontSize: 22, fontWeight: 700, marginTop: 8 }}
      >
        Sign Up with Email
      </span>

      <label className="pt-2 font-medium text-neutral-800">First Name</label>
      <input
        name="first_name"
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        value={formik.values.first_name}
        className="bg-gray-200 rounded p-2 px-8 w-full mb-1"
        placeholder="Enter your first name"
        disabled={isSubmitting}
      />
      {formik.touched.first_name && formik.errors.first_name && (
        <span className="text-red-500 text-xs mb-2">
          {formik.errors.first_name}
        </span>
      )}

      <label className="pt-2 font-medium text-neutral-800">Last Name</label>
      <input
        name="last_name"
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        value={formik.values.last_name}
        className="bg-gray-200 rounded p-2 px-8 w-full mb-1"
        placeholder="Enter your last name"
        disabled={isSubmitting}
      />
      {formik.touched.last_name && formik.errors.last_name && (
        <span className="text-red-500 text-xs mb-2">
          {formik.errors.last_name}
        </span>
      )}

      <label className="pt-2 font-medium text-neutral-800">Phone Number</label>
      <div className="flex gap-4 w-full mb-1">
        <input
          className="bg-gray-200 rounded p-2 w-1/4"
          disabled
          placeholder="IND (+91)"
        />
        {/* <input
          name="phone"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.phone}
          className="bg-gray-200 rounded p-2 px-8 w-3/4"
          placeholder="Enter your phone number"
          disabled={isSubmitting}
        /> */}

        <input
          name="phone"
          onChange={(e) => {
            const onlyDigits = e.target.value.replace(/\D/g, "").slice(0, 10);
            formik.setFieldValue("phone", onlyDigits);
          }}
          onBlur={formik.handleBlur}
          value={formik.values.phone}
          className="bg-gray-200 rounded p-2 px-8 w-3/4"
          placeholder="Enter your phone number"
          disabled={isSubmitting}
          maxLength={10}
          inputMode="numeric"
        />
      </div>
      {formik.touched.phone && formik.errors.phone && (
        <span className="text-red-500 text-xs mb-2">{formik.errors.phone}</span>
      )}

      <div className="flex gap-4 w-full mt-6" style={{ width: "80%" }}>
        <button
          type="button"
          onClick={onBack}
          disabled={isSubmitting}
          className={`py-2 px-6 rounded-lg flex-1 border-2 text-base transition-colors ${
            isSubmitting
              ? "border-gray-400 text-gray-400 cursor-not-allowed"
              : "border-purple-800 text-purple-800 hover:bg-purple-50"
          }`}
        >
          Back
        </button>
        <button
          type="submit"
          disabled={!formik.isValid || isSubmitting}
          className={`py-2 px-4 rounded-lg flex-1 flex items-center justify-center gap-2 transition-colors ${
            formik.isValid && !isSubmitting
              ? "bg-[#3B0069] hover:bg-purple-900"
              : "bg-gray-400 cursor-not-allowed"
          }`}
        >
          <span className="text-white text-base">
            {isSubmitting ? "Processing..." : "Create Account"}
          </span>
          {!isSubmitting && (
            <img src={arrow_forward} alt="" className="w-5 h-5 rounded-full" />
          )}
        </button>
      </div>
    </form>
  );
}
// Step 3: Address (street, city, pin, state)

// Main Signup Wizard
export default function SignupWizard() {
  const [step, setStep] = useState(1);

  return (
    <div
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        flexWrap: "wrap",
      }}
    >
      {/* Backgrounds */}
      <div
        style={{
          position: "absolute",
          zIndex: -3,
          height: "100%",
          width: "50%",
          top: "0px",
          left: "0px",
        }}
      >
        <img
          src={base_background}
          alt=""
          style={{
            height: "140%",
            width: "140%",
            objectFit: "cover",
            position: "relative",
            top: "-50px",
            left: "-50px",
          }}
        />
      </div>
      <div
        style={{
          position: "absolute",
          zIndex: -3,
          height: "100%",
          width: "50%",
          top: "0px",
          left: "0px",
        }}
      >
        <img
          src={arrow}
          alt=""
          style={{
            height: "115%",
            width: "115%",
            top: "0px",
            left: "-70px",
            objectFit: "contain",
            position: "relative",
            opacity: "0.5",
          }}
        />
      </div>
      <div className="flex flex-col align-middle items-center justify-center w-[50%]">
        <span className="py-5 font-extrabold text-neutral-900 text-2xl">
          <img src={main_logo} alt="" width="90%" />
        </span>
        <span
          className="font-extrabold text-neutral-900  mt-[-2]"
          style={{
            fontSize: "28px",
            color: "#ffffff",
            fontWeight: 400,
            opacity: 0.8,
            letterSpacing: "0.1em",
            textAlign: "center",
          }}
        >
          Build Smarter,Faster,Better
        </span>
      </div>

      {/* Form Column */}
      <div
        style={{
          width: "50%",
          height: "90vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          position: "relative",
          overflowY: "scroll",
          scrollbarWidth: "none",
          top: "60px",
          right: "50px",
          overflowY: "auto",
        }}
      >
        {step === 1 && <UserTypeStep onNext={() => setStep(2)} />}
        {step === 2 && <DetailsStep onBack={() => setStep(1)} />}
      </div>
    </div>
  );
}
